// src/shared/firebase/rooms.js
// Room management, game-state transitions, room listeners and room discovery.
import {
  doc, collection, setDoc, onSnapshot, deleteDoc,
  serverTimestamp, deleteField, getDoc, getDocFromServer, query, where, limit,
  increment, arrayUnion
} from 'firebase/firestore';
import { ref, onValue } from 'firebase/database';
import { nanoid } from 'nanoid';
import {
  auth, clearCanvas, db, generateAvatar, generateHint, rtdb, safeUpdateDoc, sendChatMessage, sendSystemMessage,
  setPlayerOnlineStatus,
} from './client';

// ─── Room Management ───────────────────────────────────────────────────────

export const createRoom = async (hostName, settings = {}, gameType = 'drawing') => {
  const roomId = nanoid(6).toUpperCase();
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error('Not authenticated');

  // Build settings based on game type
  const defaultSettings = gameType === 'ludo' ? { maxPlayers: 4 }
    : gameType === 'snakeladder' ? { maxPlayers: 4 }
      : gameType === 'boggle' ? { maxPlayers: 8, minWordLength: 3, roundDuration: 120 }
      : { maxPlayers: 8, rounds: 3, drawTime: 80, language: 'en' };

  const roomData = {
    id: roomId,
    hostId: userId,
    status: 'waiting',
    gameType,
    settings: { ...defaultSettings, ...settings },
    players: {
      [userId]: {
        id: userId,
        name: hostName,
        score: 0,
        isReady: true,
        isOnline: true,
        avatar: generateAvatar(hostName),
        joinedAt: Date.now(),
      }
    },
    // Drawing-specific fields (ignored by Ludo)
    currentRound: 0,
    currentDrawer: null,
    currentWord: null,
    currentWordHint: null,
    roundStartTime: null,
    createdAt: serverTimestamp(),
  };

  await setDoc(doc(db, 'rooms', roomId), roomData);
  await setPlayerOnlineStatus(roomId, userId, true);
  return roomId;
};

export const joinRoom = async (roomId, playerName) => {
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error('Not authenticated');

  const roomRef = doc(db, 'rooms', roomId);
  const roomSnap = await getDoc(roomRef);

  if (!roomSnap.exists()) throw new Error('Room not found');
  const room = roomSnap.data();

  // Allow rejoining if already a player in this room (resume after browser close)
  const isExistingPlayer = !!room.players?.[userId];
  if (isExistingPlayer) {
    await setPlayerOnlineStatus(roomId, userId, true);
    // FIX: return the already-fetched snapshot data — no second getDoc needed
    return room;
  }

  const playerCount = Object.keys(room.players || {}).length;
  if (playerCount >= room.settings.maxPlayers) throw new Error('Room is full');
  if (room.status !== 'waiting') throw new Error('Game already in progress');

  await safeUpdateDoc(roomRef, {
    [`players.${userId}`]: {
      id: userId,
      name: playerName,
      score: 0,
      isReady: true,
      isOnline: true,
      avatar: generateAvatar(playerName),
      joinedAt: Date.now(),
    }
  });

  await setPlayerOnlineStatus(roomId, userId, true);
  return room;
};

export const leaveRoom = async (roomId, userId, playerName) => {
  // Notify others before removing the player so the name is still resolvable
  if (playerName) {
    await sendSystemMessage(roomId, `${playerName} left the game`).catch(() => { });
  }
  const roomRef = doc(db, 'rooms', roomId);
  
  // Check if the leaving player is the host
  const roomSnap = await getDoc(roomRef);
  const roomData = roomSnap.data();
  
  if (roomData?.hostId === userId) {
    // Host is leaving: delete the entire room to kick everyone out
    if (roomData.gameType === 'quiz') {
      await deleteDoc(doc(db, 'rooms', roomId, 'private', 'quizKey')).catch(() => {});
    }
    await deleteDoc(roomRef);
  } else {
    // Regular player is leaving: just remove them from the players map
    await safeUpdateDoc(roomRef, {
      [`players.${userId}`]: deleteField()
    });
  }
  
  await setPlayerOnlineStatus(roomId, userId, false);
};

// ─── Game State Management ─────────────────────────────────────────────────

export const startGame = async (roomId, playerOrder) => {
  const roomRef = doc(db, 'rooms', roomId);
  await safeUpdateDoc(roomRef, {
    status: 'playing',
    currentRound: 1,
    playerOrder,
    drawerIndex: 0,
    currentDrawer: playerOrder[0],
    currentWord: null,
    currentWordHint: null,
    roundStartTime: null,
    guessedPlayers: {},
  });
};

export const selectWord = async (roomId, word) => {
  const hint = generateHint(word);
  await safeUpdateDoc(doc(db, 'rooms', roomId), {
    currentWord: word,
    currentWordHint: hint,
    status: 'playing',
    roundStartTime: serverTimestamp(),
    guessedPlayers: {},
    usedWords: arrayUnion(word),   // track used words so they aren't repeated
  });
};

export const submitGuess = async (roomId, userId, playerName, guess, currentWord) => {
  const isCorrect = guess.toLowerCase().trim() === currentWord.toLowerCase().trim();

  // Add message to chat
  await sendChatMessage(roomId, userId, playerName, guess, isCorrect ? 'correct' : 'chat');

  return isCorrect;
};

export const recordCorrectGuess = async (roomId, userId, score, timeBonus) => {
  await safeUpdateDoc(doc(db, 'rooms', roomId), {
    [`guessedPlayers.${userId}`]: { score, timeBonus, time: Date.now() },
    [`players.${userId}.score`]: increment(score),   // ← ADD to existing score, not replace
  });
};

export const updateDrawerScore = async (roomId, drawerId, bonus) => {
  await safeUpdateDoc(doc(db, 'rooms', roomId), {
    [`players.${drawerId}.score`]: increment(bonus), // ← atomic increment, no race condition
  });
};

export const advanceRound = async (roomId, playerOrder, drawerIndex, currentRound, totalRounds) => {
  const nextDrawerIndex = (drawerIndex + 1) % playerOrder.length;
  const nextRound = nextDrawerIndex === 0 ? currentRound + 1 : currentRound;

  if (nextRound > totalRounds) {
    await safeUpdateDoc(doc(db, 'rooms', roomId), {
      status: 'finished',
      currentDrawer: null,
      currentWord: null,
      currentWordHint: null,
    });
    return false;
  }

  await safeUpdateDoc(doc(db, 'rooms', roomId), {
    status: 'selectingWord',
    currentRound: nextRound,
    drawerIndex: nextDrawerIndex,
    currentDrawer: playerOrder[nextDrawerIndex],
    currentWord: null,
    currentWordHint: null,
    roundStartTime: null,
    guessedPlayers: {},
  });
  return true;
};

export const endRound = async (roomId) => {
  await safeUpdateDoc(doc(db, 'rooms', roomId), {
    status: 'roundEnd',
  });
};

export const resetRoom = async (roomId, hostId) => {
  const roomSnap = await getDoc(doc(db, 'rooms', roomId));
  const room = roomSnap.data();
  const resetPlayers = {};
  Object.keys(room.players).forEach(pid => {
    resetPlayers[pid] = { ...room.players[pid], score: 0 };
  });
  await safeUpdateDoc(doc(db, 'rooms', roomId), {
    status: 'waiting',
    currentRound: 0,
    currentDrawer: null,
    currentWord: null,
    currentWordHint: null,
    roundStartTime: null,
    guessedPlayers: {},
    usedWords: [],              // reset word history for the new session
    players: resetPlayers,
  });
  await clearCanvas(roomId);
};

// ─── Room Listener ─────────────────────────────────────────────────────────

export const listenRoom = (roomId, callback) => {
  return onSnapshot(doc(db, 'rooms', roomId), (snap) => {
    if (snap.exists()) callback(snap.data());
    else callback(null);
  });
};

export const refreshRoomFromServer = async (roomId) => {
  const snap = await getDocFromServer(doc(db, 'rooms', roomId));
  return snap.exists() ? snap.data() : null;
};

// ─── Open Rooms / Active Games Discovery ───────────────────────────────────

/**
 * Listen to rooms that are still waiting (not started).
 * Filters client-side to rooms where host is online via RTDB presence.
 */
export const listenOpenRooms = (callback) => {
  const q = query(
    collection(db, 'rooms'),
    where('status', '==', 'waiting'),
    limit(30)
  );

  // FIX: in-flight guard — if the Firestore snapshot fires again before the previous
  // batch of RTDB onValue calls has resolved, skip the new snapshot instead of
  // spawning a second batch of N listeners for the same rooms.
  let inFlight = false;

  const unsubFirestore = onSnapshot(q, async (snap) => {
    if (inFlight) return;
    inFlight = true;
    try {
      const rooms = snap.docs.map(d => d.data());
      const checks = await Promise.all(rooms.map(room => {
        return new Promise(resolve => {
          if (!room.hostId || !room.id) { resolve(null); return; }
          const presRef = ref(rtdb, `presence/${room.id}/${room.hostId}`);
          onValue(presRef, (pSnap) => {
            resolve(pSnap.val()?.online === true ? room : null);
          }, { onlyOnce: true });
        });
      }));
      callback(checks.filter(Boolean));
    } finally {
      inFlight = false;
    }
  });

  return unsubFirestore;
};

export const listenActiveGames = (callback) => {
  const q = query(
    collection(db, 'rooms'),
    where('status', '==', 'playing'),
    limit(10)
  );

  // FIX: in-flight guard prevents a new RTDB listener batch from spawning
  // before the previous one resolves (same pattern as listenOpenRooms).
  let inFlight = false;

  return onSnapshot(q, async (snap) => {
    if (inFlight) return;
    inFlight = true;
    try {
      const rooms = snap.docs.map(d => d.data());

      const results = await Promise.all(rooms.map(room =>
        new Promise(resolve => {
          const playerIds = Object.keys(room.players || {});

          // No players — hide from UI.
          // FIX: do NOT deleteDoc inside a snapshot callback — it triggers another
          // snapshot, can cascade, and permanently deletes rooms whose players are
          // mid-reconnect. Room cleanup should be handled by Cloud Functions / TTL rules.
          if (playerIds.length === 0) { resolve(null); return; }

          const presenceRef = ref(rtdb, `presence/${room.id}`);
          onValue(presenceRef, (pSnap) => {
            const presence  = pSnap.val() || {};
            const anyOnline = playerIds.some(uid => presence[uid]?.online === true);
            // FIX: just filter from the UI — don't delete
            resolve(anyOnline ? room : null);
          }, { onlyOnce: true });
        })
      ));

      callback(results.filter(Boolean));
    } finally {
      inFlight = false;
    }
  });
};
